import { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { parse, stringify, type ParsedQuery } from "query-string";
import { SEPARATOR } from "common/data/url/index";

const parseQuery = () =>
  parse(location.search, { arrayFormat: "separator", arrayFormatSeparator: SEPARATOR });

const stringifyQuery = (params: ParsedQuery) =>
  stringify(params, {
    arrayFormat: "separator",
    arrayFormatSeparator: SEPARATOR,
    skipEmptyString: true,
  });

export function formatQueryParamValue(value: string | string[] | null): string {
  return Array.isArray(value) ? value.join(SEPARATOR) : String(value);
}

const generateSetQueryParam =
  (property: string, history: ReturnType<typeof useHistory>) =>
  (input?: string | string[]): void => {
    const params = parseQuery();
    let res: ParsedQuery = {};
    if (!input || (Array.isArray(input) && input.length === 0)) {
      // remove the property we're updating
      Object.entries(params).forEach(([key, value]) => {
        if (key !== property) {
          res[key] = value;
        }
      });
    } else {
      res = params;
      res[property] = formatQueryParamValue(input);
    }

    history.push({ search: `?${stringifyQuery(res)}` });
  };

export function useQueryParam(
  property: string,
): [string, ReturnType<typeof generateSetQueryParam>] {
  const location = useLocation();
  const history = useHistory();
  const [params, updateParams] = useState(parseQuery());
  const setQueryParam = generateSetQueryParam(property, history);

  useEffect(() => {
    updateParams(parseQuery());
  }, [location, history]);

  const value = params[property] ?? "";
  const formattedValue = formatQueryParamValue(value);

  return [formattedValue, setQueryParam];
}

export function useQueryParamArray(
  property: string,
): [string[], ReturnType<typeof generateSetQueryParam>] {
  const location = useLocation();
  const history = useHistory();
  const [params, updateParams] = useState(parseQuery());
  const setQueryParam = generateSetQueryParam(property, history);

  useEffect(() => {
    updateParams(parseQuery());
  }, [location, history]);

  const value = params[property] ?? [];
  const formattedValue = Array.isArray(value) ? value : [value];

  return [formattedValue, setQueryParam];
}

export function useQueryParams(): [ParsedQuery] {
  const location = useLocation();
  const history = useHistory();
  const [params, updateParams] = useState(parseQuery());

  useEffect(() => {
    updateParams(parseQuery());
  }, [location, history]);

  return [params];
}
